import {
  addDaysToDateString,
  getWeekDateStrings,
  toLocalDateString,
} from '../../utils/timesheetDates';

export const hourValue = (value) => Number.parseFloat(value) || 0;

export const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export const emptyDayHours = () => ({ reg: '', t12: '', dbl: '', comment: '' });

export const dayTotal = (day = {}) => hourValue(day.reg) + hourValue(day.t12) + hourValue(day.dbl);

export const weekTotal = (days = {}) => Object.values(days).reduce((sum, day) => sum + dayTotal(day), 0);

export const currentDateString = () => toLocalDateString(new Date());

export const crewPeople = (crew) => {
  if (!crew) return [];
  const people = [];
  if (crew.head_emp_code) {
    people.push({
      emp_code: crew.head_emp_code,
      emp_name: crew.head_emp_name || '',
      trade: crew.head_trade || '',
      division: crew.head_division || '',
    });
  }
  (crew.members || []).forEach((member) => {
    if (member.emp_code && member.emp_code !== crew.head_emp_code) {
      people.push({
        emp_code: member.emp_code,
        emp_name: member.emp_name || '',
        trade: member.trade || '',
        division: member.division || '',
      });
    }
  });
  return people;
};

export const defaultCrewProject = (crew) => {
  if (crew?.projects?.length === 1) return crew.projects[0];
  return null;
};

export const inferDisciplineCode = (project) => {
  if (project?.discipline_code) return project.discipline_code;
  const fromList = project?.disciplines?.find((item) => item?.discipline_code)?.discipline_code;
  if (fromList) return fromList;
  const suffix = String(project?.job_number || '').match(/([A-Za-z]{3})$/);
  return suffix ? suffix[1].toUpperCase() : '';
};

export const createCrewRow = (person, project = null) => ({
  id: `crew-${person.emp_code}-${Date.now()}-${Math.random().toString(16).slice(2)}`,
  emp_code: person.emp_code,
  emp_name: person.emp_name,
  trade: person.trade || '',
  division: person.division || '',
  project_id: project?.project_id || project?.id || '',
  job_number: project?.job_number || '',
  discipline_code: inferDisciplineCode(project),
  op_code: '',
  op_code_description: '',
  reg: '',
  t12: '',
  dbl: '',
  rnb: '',
  meals: '',
  travel_2: '',
  comment: '',
});

export const createWeeklyLine = (project = null) => ({
  id: `week-${Date.now()}-${Math.random().toString(16).slice(2)}`,
  project_id: project?.id || project?.project_id || '',
  job_number: project?.job_number || '',
  discipline_code: inferDisciplineCode(project),
  op_code: '',
  op_code_description: '',
  days: {},
});

export const LAST_WEEKLY_LINE_KEY = 'lgl-m-tt-last-line';

export const lineHasJobAndCode = (line) => Boolean(line?.project_id && line?.op_code);

export const snapshotWeeklyLine = (line) => {
  if (!lineHasJobAndCode(line)) return null;
  return {
    project_id: line.project_id,
    job_number: line.job_number || '',
    discipline_code: line.discipline_code || '',
    op_code: line.op_code,
    op_code_description: line.op_code_description || '',
  };
};

export const lastFilledWeeklyLine = (lines = []) => {
  for (let index = lines.length - 1; index >= 0; index -= 1) {
    if (lineHasJobAndCode(lines[index])) return lines[index];
  }
  return null;
};

export const repeatWeeklyLine = (line) => {
  const next = createWeeklyLine({
    id: line?.project_id,
    project_id: line?.project_id,
    job_number: line?.job_number,
    discipline_code: line?.discipline_code,
  });
  return {
    ...next,
    op_code: line?.op_code || '',
    op_code_description: line?.op_code_description || '',
  };
};

export const isEmptyWeeklySheet = (lines = []) => (
  !lines.some((line) => lineHasJobAndCode(line) || weekTotal(line.days) > 0)
);

export const applyRepeatedLine = (lines, last) => {
  if (!last || !lineHasJobAndCode(last)) return lines;
  const next = repeatWeeklyLine(last);
  if (!lines.length || isEmptyWeeklySheet(lines)) return [next];
  return [...lines, next];
};

export const copyWeekJobCodes = (entries = []) => (
  groupWeekEntries(entries).filter(lineHasJobAndCode).map((line) => repeatWeeklyLine(line))
);

export const formatJobCodeLabel = (line) => {
  if (!lineHasJobAndCode(line)) return '';
  return `${line.job_number || 'Job'} · ${line.op_code}`;
};

export const rememberLastWeeklyLine = (line, storage) => {
  const snapshot = snapshotWeeklyLine(line);
  if (!snapshot) return null;
  const store = storage || (typeof window !== 'undefined' ? window.localStorage : null);
  try {
    store?.setItem(LAST_WEEKLY_LINE_KEY, JSON.stringify(snapshot));
  } catch {
    return snapshot;
  }
  return snapshot;
};

export const readLastWeeklyLine = (storage) => {
  const store = storage || (typeof window !== 'undefined' ? window.localStorage : null);
  try {
    const raw = store?.getItem(LAST_WEEKLY_LINE_KEY);
    if (!raw) return null;
    return snapshotWeeklyLine(JSON.parse(raw));
  } catch {
    return null;
  }
};

export const hoursForDate = (line, dateStr) => line.days?.[dateStr] || emptyDayHours();

export const setLineDayHours = (line, dateStr, field, value) => ({
  ...line,
  days: {
    ...line.days,
    [dateStr]: {
      ...hoursForDate(line, dateStr),
      [field]: value,
    },
  },
});

export const groupWeekEntries = (entries = []) => {
  const grouped = {};
  entries.forEach((entry) => {
    const dateStr = String(entry.date || '').split('T')[0];
    const key = `${entry.project_id || ''}::${entry.op_code || ''}`;
    if (!grouped[key]) {
      grouped[key] = {
        id: `loaded-${key}`,
        project_id: entry.project_id || '',
        job_number: entry.job_number || '',
        discipline_code: entry.discipline_code || inferDisciplineCode(entry),
        op_code: entry.op_code || '',
        op_code_description: entry.op_code_description || '',
        days: {},
      };
    }
    grouped[key].days[dateStr] = {
      reg: entry.reg || '',
      t12: entry.t12 || '',
      dbl: entry.dbl || '',
      comment: entry.comment || '',
      id: entry.id || null,
    };
  });
  return Object.values(grouped);
};

export const emptyAllowances = () => ({ rnb: '', meals: '', travel_2: '' });

export const allowanceValue = (value) => Number.parseFloat(value) || 0;

export const allowanceTotal = (allowance = {}) => (
  allowanceValue(allowance.rnb) + allowanceValue(allowance.meals) + allowanceValue(allowance.travel_2)
);

export const formatMoney = (value) => {
  const amount = allowanceValue(value);
  if (!amount) return '';
  return Number.isInteger(amount) ? `$${amount}` : `$${amount.toFixed(2)}`;
};

export const isSameEmployee = (left, right) => (
  Boolean(left?.emp_code) && String(left.emp_code) === String(right?.emp_code)
);

export const mapEmployee = (emp) => {
  if (!emp) return null;
  return {
    emp_code: emp.emp_code || emp.empl_code || '',
    emp_name: emp.emp_name || emp.empl_name || '',
    trade: emp.trade || '',
    division: emp.division || '',
  };
};

export const filterEmployees = (people = [], query = '', limit = 12) => {
  const needle = query.trim().toLowerCase();
  const pool = !needle
    ? people
    : people.filter((person) => (
      String(person.emp_name || '').toLowerCase().includes(needle)
      || String(person.emp_code || '').toLowerCase().includes(needle)
    ));
  return pool.slice(0, limit);
};

export const sumAllowancesByDate = (entries = []) => {
  const totals = {};
  entries.forEach((entry) => {
    const dateStr = String(entry.date || '').split('T')[0];
    if (!dateStr) return;
    const current = totals[dateStr] || { rnb: 0, meals: 0, travel_2: 0 };
    current.rnb += allowanceValue(entry.r_and_b ?? entry.rnb);
    current.meals += allowanceValue(entry.meals);
    current.travel_2 += allowanceValue(entry.travel_2);
    totals[dateStr] = current;
  });
  return Object.fromEntries(Object.entries(totals).map(([date, amount]) => [date, {
    rnb: amount.rnb ? String(amount.rnb) : '',
    meals: amount.meals ? String(amount.meals) : '',
    travel_2: amount.travel_2 ? String(amount.travel_2) : '',
  }]));
};

export const distributeAllowances = (rows, totals = emptyAllowances()) => {
  const totalHours = rows.reduce((sum, row) => sum + dayTotal(row), 0);
  const rnb = allowanceValue(totals.rnb);
  const meals = allowanceValue(totals.meals);
  const travel = allowanceValue(totals.travel_2);
  if (!rows.length || totalHours === 0) {
    return rows.map(() => ({ rnb: 0, meals: 0, travel_2: 0 }));
  }
  const distributed = rows.map((row) => {
    const frac = dayTotal(row) / totalHours;
    return {
      rnb: Math.round(rnb * frac * 100) / 100,
      meals: Math.round(meals * frac * 100) / 100,
      travel_2: Math.round(travel * frac * 100) / 100,
    };
  });
  ['rnb', 'meals', 'travel_2'].forEach((key) => {
    const total = { rnb, meals, travel_2: travel }[key];
    const diff = Math.round((total - distributed.reduce((sum, row) => sum + row[key], 0)) * 100) / 100;
    if (diff === 0) return;
    const maxIdx = distributed.reduce((best, _, index) => (
      dayTotal(rows[index]) > dayTotal(rows[best]) ? index : best
    ), 0);
    distributed[maxIdx][key] = Math.round((distributed[maxIdx][key] + diff) * 100) / 100;
  });
  return distributed;
};

export const buildWeekEntries = (lines, weekDates, employee, dailyAllowances = {}) => {
  if (!employee?.emp_code) return [];
  const byDate = {};
  lines.forEach((line) => {
    if (!line.project_id || !line.op_code) return;
    weekDates.forEach((day) => {
      const hours = hoursForDate(line, day.dateStr);
      if (dayTotal(hours) <= 0) return;
      (byDate[day.dateStr] ||= []).push({ line, day, hours });
    });
  });

  const entries = [];
  Object.entries(byDate).forEach(([dateStr, items]) => {
    const shares = distributeAllowances(
      items.map((item) => item.hours),
      dailyAllowances[dateStr] || emptyAllowances()
    );
    items.forEach((item, index) => {
      entries.push({
        id: item.hours.id || null,
        project_id: item.line.project_id,
        discipline_code: item.line.discipline_code || null,
        date: dateStr,
        empl_code: employee.emp_code,
        empl_name: employee.emp_name,
        trade: employee.trade || '',
        division: employee.division || '',
        op_code: item.line.op_code,
        op_code_description: item.line.op_code_description || '',
        reg: hourValue(item.hours.reg),
        t12: hourValue(item.hours.t12),
        dbl: hourValue(item.hours.dbl),
        height: 0,
        rnb: shares[index].rnb,
        meals: shares[index].meals,
        travel_2: shares[index].travel_2,
        shift: 'D',
        comment: item.hours.comment || null,
      });
    });
  });
  return entries;
};

export const buildCrewEntries = (rows, date) => rows
  .filter((row) => row.emp_code && row.project_id && row.op_code && dayTotal(row) > 0)
  .map((row) => ({
    date,
    project_id: row.project_id,
    discipline_code: row.discipline_code || null,
    empl_code: row.emp_code,
    empl_name: row.emp_name,
    trade: row.trade || '',
    division: row.division || '',
    op_code: row.op_code,
    op_code_description: row.op_code_description || '',
    reg: hourValue(row.reg),
    t12: hourValue(row.t12),
    dbl: hourValue(row.dbl),
    height: 0,
    rnb: hourValue(row.rnb),
    meals: hourValue(row.meals),
    travel_2: hourValue(row.travel_2),
    shift: 'D',
    comment: row.comment || null,
  }));

export const dailyTotalsByEmployee = (rows) => {
  const totals = {};
  rows.forEach((row) => {
    if (!row.emp_code) return;
    totals[row.emp_code] = (totals[row.emp_code] || 0) + dayTotal(row);
  });
  return totals;
};

export const overDailyLimit = (totals, limit = 24) => (
  Object.entries(totals).find(([, hours]) => hours > limit) || null
);

export const formatWeekRange = (weekDates = []) => {
  if (!weekDates.length) return '';
  const first = weekDates[0];
  const last = weekDates[weekDates.length - 1];
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[first.month]} ${first.dayNum} – ${months[last.month]} ${last.dayNum}`;
};

export const shiftWeek = (dateStr, days) => addDaysToDateString(dateStr, days);

export const weekForDate = (dateStr, weekStartDay = 0) => getWeekDateStrings(dateStr, weekStartDay);

export const normalizeList = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.entries)) return data.entries;
  if (Array.isArray(data?.rows)) return data.rows;
  return [];
};

export const catalogueLabourCodes = (allCodes = []) => {
  const labour = allCodes.filter((code) => code.costType === 'L' && code.type === 'Activity');
  const discipline = labour.filter((code) => code.group === 'Discipline Labour');
  return discipline.length ? discipline : labour;
};

export const normalizeOpCodes = (rows = []) => {
  const seen = new Set();
  const codes = [];
  rows.forEach((row) => {
    const code = String(row.op_code || row.code || '').trim();
    if (!code || seen.has(code)) return;
    seen.add(code);
    codes.push({
      code,
      description: row.op_description || row.op_code_description || row.description || '',
    });
  });
  return codes;
};

export const filterOpCodes = (codes = [], query = '', limit = 12) => {
  const needle = query.trim().toLowerCase();
  const pool = !needle
    ? codes
    : codes.filter((code) => (
      code.code.toLowerCase().includes(needle)
      || String(code.description || '').toLowerCase().includes(needle)
    ));
  return pool.slice(0, limit);
};

export const applyJobAndCode = (rows, fields) => rows.map((row) => ({ ...row, ...fields }));

export const applyRegularHours = (rows, hours = '8') => rows.map((row) => ({ ...row, reg: hours }));

export const applyAllowances = (rows, amounts = emptyAllowances()) => rows.map((row) => ({
  ...row,
  rnb: amounts.rnb ?? '',
  meals: amounts.meals ?? '',
  travel_2: amounts.travel_2 ?? '',
}));

export const appendNumeric = (current, key) => {
  const value = String(current ?? '');
  if (key === 'back') return value.slice(0, -1);
  if (key === '.') {
    if (value.includes('.')) return value;
    return value === '' ? '0.' : `${value}.`;
  }
  if (!/^\d$/.test(String(key))) return value;
  if (value === '0') return String(key);
  return `${value}${key}`;
};

export const HOUR_FIELDS = ['reg', 't12', 'dbl'];
export const ALLOWANCE_FIELDS = ['rnb', 'meals', 'travel_2'];

export const padKey = (focus) => {
  if (!focus?.kind || !focus.field) return '';
  return [focus.kind, focus.id || '', focus.date || '', focus.field].join('|');
};

export const weeklyPadChain = (lineId, dateStr) => [
  ...HOUR_FIELDS.map((field) => ({ kind: 'weekHour', id: lineId, date: dateStr, field })),
  ...ALLOWANCE_FIELDS.map((field) => ({ kind: 'weekAllow', id: lineId, date: dateStr, field })),
];

export const dailyPadChain = (rows = []) => [
  ...ALLOWANCE_FIELDS.map((field) => ({ kind: 'crewShare', field })),
  ...rows.flatMap((row) => [
    ...HOUR_FIELDS.map((field) => ({ kind: 'crewHour', id: row.id, field })),
    ...ALLOWANCE_FIELDS.map((field) => ({ kind: 'crewAllow', id: row.id, field })),
  ]),
];

export const nextPadFocus = (chain, current) => {
  const key = padKey(current);
  const index = chain.findIndex((item) => padKey(item) === key);
  if (index < 0 || index >= chain.length - 1) return null;
  return chain[index + 1];
};

export const linesWeekTotal = (lines = []) => lines.reduce((sum, line) => sum + weekTotal(line.days), 0);

export const formatDayHoursLabel = (total, money) => {
  if (!total && !money) return '—';
  return `${total ? `${total.toFixed(1)}h` : '—'}${money ? ` · ${money}` : ''}`;
};

export const weekCellLabel = (day, total, money) => {
  const name = `${DAY_LABELS[day.dayOfWeek]} ${day.dayNum}`;
  if (!total && !money) return name;
  return `${name}, ${formatDayHoursLabel(total, money)}`;
};

export const weekReviewDays = (line, weekDates, dailyAllowances = {}) => (
  weekDates.map((day) => {
    const total = dayTotal(hoursForDate(line, day.dateStr));
    const money = formatMoney(allowanceTotal(dailyAllowances[day.dateStr]));
    return {
      date: day.dateStr,
      label: `${DAY_LABELS[day.dayOfWeek]} ${day.dayNum}`,
      hours: total,
      text: formatDayHoursLabel(total, money),
    };
  }).filter((day) => day.hours > 0)
);

export const weekReviewAllowances = (weekDates, dailyAllowances = {}) => (
  weekDates.map((day) => ({
    label: `${DAY_LABELS[day.dayOfWeek]} ${day.dayNum}`,
    money: formatMoney(allowanceTotal(dailyAllowances[day.dateStr])),
    total: allowanceTotal(dailyAllowances[day.dateStr]),
  })).filter((day) => day.total)
);

export const SHORT_DAY_LABELS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

export const compactNumber = (value) => {
  const amount = Number.parseFloat(value) || 0;
  if (!amount) return '—';
  return Number.isInteger(amount) ? String(amount) : String(Number(amount.toFixed(1)));
};

export const weekReviewTable = (lines = [], weekDates = [], dailyAllowances = {}) => {
  const hourRows = lines
    .filter((line) => lineHasJobAndCode(line) || weekTotal(line.days) > 0)
    .map((line) => ({
      id: line.id,
      label: `${line.job_number || 'Job'} · ${line.op_code || '—'}`,
      kind: 'hours',
      cells: weekDates.map((day) => compactNumber(dayTotal(hoursForDate(line, day.dateStr)))),
    }));
  const allowRows = [
    ['rnb', 'R&B'],
    ['meals', 'Meals'],
    ['travel_2', 'Travel'],
  ].map(([field, label]) => {
    const cells = weekDates.map((day) => compactNumber((dailyAllowances[day.dateStr] || {})[field]));
    return {
      id: field,
      label,
      kind: 'allow',
      cells,
      empty: !cells.some((cell) => cell !== '—'),
    };
  }).filter((row) => !row.empty);
  return {
    dayHeads: weekDates.map((day) => ({
      id: day.dateStr,
      label: SHORT_DAY_LABELS[day.dayOfWeek] || DAY_LABELS[day.dayOfWeek],
      num: day.dayNum,
    })),
    rows: [...hourRows, ...allowRows],
  };
};

export const crewReviewTable = (rows = []) => (
  rows
    .filter((row) => dayTotal(row) > 0)
    .map((row) => ({
      id: row.id,
      label: row.emp_name || row.emp_code,
      cells: [
        compactNumber(dayTotal(row)),
        compactNumber(row.rnb),
        compactNumber(row.meals),
        compactNumber(row.travel_2),
      ],
    }))
);

export const CREW_REVIEW_HEADS = ['Hrs', 'R&B', 'Meal', 'Trv'];
